import {
  CircularProgress,
  InputAdornment,
  Paper,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSerialisedSubAssemblies } from "../Utils/ApiCalls";
import { getLCLocation, getLatestLifeCycleState } from "../Utils/Common";

const getLatestLocationLabel = (option) => {
  const latest = getLatestLifeCycleState(option?.LifeCycleStates || []);
  if (!latest?.location) {
    return "";
  }
  return getLCLocation(latest.location) || latest.location;
};

const getValidStockItemId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

export default function SerialisedSubAssemblySearchAutocomplete({
  onChange,
  searchRestrictions,
  fetchOnFocus,
  excludeIds,
  label,
}) {
  const [subAssembliesList, setSubAssembliesList] = useState([]);
  const [loadingSubAssembliesList, setLoadingSubAssembliesList] =
    useState(false);
  const [typing, setTyping] = useState();
  const abortcontroller = useRef();
  const { t } = useTranslation();
  const filter = createFilterOptions();
  const excluded = new Set((excludeIds || []).map((id) => id && id.toString()));
  const stockItemIdFilter = getValidStockItemId(
    searchRestrictions?.StockItemId,
  );
  const getSubAssemblyStockItemId = (subAssembly) =>
    subAssembly?.StockItemId ||
    subAssembly?.ManufacturingEntry?.StockItemId ||
    subAssembly?.ManufacturingEntry?.StockItem?.id;
  const hasMatchingStockItemLink = (subAssembly) =>
    stockItemIdFilter &&
    getSubAssemblyStockItemId(subAssembly)?.toString() ===
      stockItemIdFilter.toString();

  const fetchSubAssemblies = (searchValue) => {
    if (!stockItemIdFilter) {
      setSubAssembliesList([]);
      return;
    }
    if (!searchValue && !fetchOnFocus && !searchRestrictions?.lcLocation) {
      return;
    }
    if (abortcontroller.current) {
      abortcontroller.current.abort("User altered the search parameters");
    }
    abortcontroller.current = new AbortController();
    setLoadingSubAssembliesList(true);
    getSerialisedSubAssemblies(
      {
        view: "search",
        search_value: searchValue || "",
        ShipmentId: null,
        DeviceId: null,
        ...(searchRestrictions || {}),
        StockItemId: stockItemIdFilter,
      },
      (result) => {
        if (!result.error) {
          setSubAssembliesList(
            (result.serialisedSubAssemblies || []).filter(
              (item) =>
                item?.id &&
                !item.DeviceId &&
                hasMatchingStockItemLink(item) &&
                !excluded.has(item.id.toString()),
            ),
          );
        }
        setLoadingSubAssembliesList(false);
      },
      abortcontroller.current,
    );
  };

  return (
    <Grid item xs={12}>
      <Autocomplete
        id="serialised-sub-assemblies"
        options={subAssembliesList}
        fullWidth
        PaperComponent={(props) => (
          <Paper style={{ border: "2px solid #3d57a5" }} {...props} />
        )}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return option?.number || "";
        }}
        renderOption={(option) => (
          <React.Fragment key={option.id || option.number}>
            <Grid container>
              <Grid item xs={12}>
                <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                  {[option.number, option.color].filter(Boolean).join(" - ")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontSize: 12 }}>
                  {[option.model, getLatestLocationLabel(option)]
                    .filter(Boolean)
                    .join(" - ")}
                </Typography>
              </Grid>
            </Grid>
          </React.Fragment>
        )}
        filterOptions={(options, params) =>
          filter(options, {
            ...params,
            inputValue: params.inputValue.replace(" ", ""),
          })
        }
        loading={loadingSubAssembliesList}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              label ||
              t(
                "shipments.searchSerialisedSubAssembly",
                "Search serialised sub-assembly",
              )
            }
            placeholder={t("serialisedSubAssembly.number", "Chassis number")}
            variant="outlined"
            InputProps={
              loadingSubAssembliesList
                ? {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }
                : { ...params.InputProps }
            }
          />
        )}
        onFocus={() => {
          if (fetchOnFocus) {
            fetchSubAssemblies("");
          }
        }}
        onChange={onChange}
        onChangeCapture={(e) => {
          const value = e.target.value
            ? e.target.value.toLowerCase().replace(/\s/g, "")
            : "";
          if (typing) {
            clearTimeout(typing);
            setTyping();
          }
          const timeout = setTimeout(
            () => {
              fetchSubAssemblies(value);
            },
            typing ? 500 : 1,
          );
          setTyping(timeout);
        }}
      />
    </Grid>
  );
}
